import type {
  TutorialOverlayPadding,
  TutorialOverlayProps,
  TutorialRef,
  TutorialTargetRect,
  TutorialTargetRef,
  TutorialTargetRegistrations,
} from 'app/types/tutorial';

const RECT_EPSILON = 1;

export function addTutorialTargetPadding(
  rect: TutorialTargetRect,
  padding?: TutorialOverlayPadding,
): TutorialTargetRect {
  const paddingHorizontal = padding?.paddingHorizontal ?? 0;
  const paddingVertical = padding?.paddingVertical ?? 0;
  return {
    x: rect.x - paddingHorizontal,
    y: rect.y - paddingVertical,
    width: rect.width + paddingHorizontal * 2,
    height: rect.height + paddingVertical * 2,
  };
}

export function areTutorialTargetRectsEqual(first: TutorialTargetRect, second: TutorialTargetRect) {
  return (
    Math.abs(first.x - second.x) <= RECT_EPSILON &&
    Math.abs(first.y - second.y) <= RECT_EPSILON &&
    Math.abs(first.width - second.width) <= RECT_EPSILON &&
    Math.abs(first.height - second.height) <= RECT_EPSILON
  );
}

export function isTutorialTargetInViewport(rect: TutorialTargetRect, viewportWidth: number, viewportHeight: number) {
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.x < viewportWidth &&
    rect.y < viewportHeight &&
    rect.x + rect.width > 0 &&
    rect.y + rect.height > 0
  );
}

export function getFocusedTutorialTarget(
  registrations: TutorialTargetRegistrations,
  targetRef?: TutorialRef,
): TutorialTargetRef | undefined {
  if (!targetRef) return undefined;

  return [...(registrations[targetRef] ?? [])].reverse().find((registration) => registration.isFocused)?.ref;
}

export function isTutorialOverlayRouteActive(options: TutorialOverlayProps | undefined, currentRouteKey: string) {
  return Boolean(options) && (!options?.ownerRouteKey || options.ownerRouteKey === currentRouteKey);
}

export function canPresentTutorialOverlay(
  options: TutorialOverlayProps | undefined,
  currentRouteKey: string,
  targetRect?: TutorialTargetRect,
) {
  if (!isTutorialOverlayRouteActive(options, currentRouteKey)) return false;
  return !options?.pointing || Boolean(targetRect);
}

export function shouldResumeTutorialOverlay(canPresent: boolean, isVisible: boolean, isExplicitlyHiding: boolean) {
  return canPresent && !isVisible && !isExplicitlyHiding;
}
